import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Card } from '@/components/ui/Card';
import { ListItem } from '@/components/ui/ListItem';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSupportTickets } from '@/api/queries/support';
import { useLocale } from '@/hooks/useLocale';
import { formatDate } from '@/utils/date';
import { paths } from '@/routes/paths';

export function SupportListPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const tickets = useSupportTickets();

  return (
    <AppShell>
      <PageContainer paddingX="md">
        <TopBar back title={t('support.title')} />
        <Button
          block
          size="lg"
          variant="inverted"
          onClick={() => navigate(paths.supportNew)}
        >
          {t('support.new_ticket')}
        </Button>

        {tickets.isLoading && (
          <div style={{ display: 'grid', placeItems: 'center', padding: 32 }}>
            <Spinner />
          </div>
        )}

        {!tickets.isLoading && !tickets.data?.length && (
          <EmptyState
            title={t('support.empty_title')}
            body={t('support.empty_body')}
          />
        )}

        {!!tickets.data?.length && (
          <Card variant="light" padding="md">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {tickets.data.map((tk) => (
                <ListItem
                  key={tk.id}
                  title={tk.subject}
                  subtitle={`${t(`support.status.${tk.status}`)} · ${formatDate(tk.updated_at, locale)}`}
                  trailing="›"
                  onClick={() => navigate(paths.supportTicket(tk.id))}
                />
              ))}
            </div>
          </Card>
        )}
      </PageContainer>
    </AppShell>
  );
}
